import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Phone,
  MapPin,
  Clock,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  Menu,
  X,
  ShieldCheck,
  Truck,
  Languages,
  Calendar,
  Check,
  Sparkles,
  Users,
  CreditCard,
  Lock,
  Zap,
  Facebook,
  Navigation,
  Mail,
  Send,
} from "lucide-react";
import "@/App.css";
import "@/i18n";
import { LANGS } from "@/i18n";
import {
  LOGO_URL,
  HERO_IMAGES,
  GALLERY_IMAGES,
  MOTORCYCLES,
  PACKAGES,
  WHATSAPP_DISPLAY,
  WHATSAPP_LINK,
  buildWhatsappLink,
  LOCATION,
} from "@/data/content";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

/* --------------------------- Motion helpers --------------------------- */
// Typewriter hook — types out `text` char-by-char, restarts when text changes.
const useTypewriter = (text, speed = 55, startDelay = 150) => {
  const [out, setOut] = useState("");
  useEffect(() => {
    setOut("");
    if (!text) return;
    let i = 0;
    let raf;
    const start = setTimeout(function tick() {
      setOut(text.slice(0, i + 1));
      i += 1;
      if (i < text.length) raf = setTimeout(tick, speed);
    }, startDelay);
    return () => {
      clearTimeout(start);
      clearTimeout(raf);
    };
  }, [text, speed, startDelay]);
  return out;
};

const Typewriter = ({ text, className = "", speed, delay, caret = true }) => {
  const out = useTypewriter(text, speed, delay);
  const done = out.length === (text?.length || 0);
  return (
    <span className={className}>
      {out}
      {caret && (
        <span
          className={`inline-block w-[3px] ml-1 align-baseline bg-current ${
            done ? "animate-pulse opacity-70" : "opacity-100"
          }`}
          style={{ height: "0.85em" }}
        />
      )}
    </span>
  );
};

// Section reveal — fly in from below on scroll, stagger children.
const revealParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};
const revealChild = {
  hidden: { opacity: 0, y: 40, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

const Reveal = ({ children, className = "", as: Tag = motion.div, ...rest }) => (
  <Tag
    className={className}
    variants={revealParent}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    {...rest}
  >
    {children}
  </Tag>
);

const RevealItem = ({ children, className = "", as: Tag = motion.div, ...rest }) => (
  <Tag className={className} variants={revealChild} {...rest}>
    {children}
  </Tag>
);

/* -------------------------- Language Switcher -------------------------- */
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const current =
    LANGS.find((l) => l.code === i18n.resolvedLanguage) || LANGS[1];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="lang-switcher-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-full border border-neutral-800/20 hover:bg-neutral-900 hover:text-white transition text-sm font-medium"
        >
          <Languages className="w-4 h-4" />
          <span>{current.flag}</span>
          <span className="hidden sm:inline">{current.label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`lang-option-${l.code}`}
            onClick={() => i18n.changeLanguage(l.code)}
            className="cursor-pointer"
          >
            <span className="mr-2">{l.flag}</span>
            {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/* ------------------------------ Nav ------------------------------ */
const NavBar = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const links = [
    ["motos", "moto"],
    ["packages", "packages"],
    ["rates", "rates"],
    ["gallery", "gallery"],
    ["about", "about"],
    ["contact", "contact"],
  ];
  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-white/85 backdrop-blur-md border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-20">
        <a
          href="#home"
          data-testid="nav-logo"
          className="flex items-center gap-2"
        >
          <img
            src={LOGO_URL}
            alt="Jhossy Moto Rental"
            className="h-14 sm:h-16 w-auto"
          />
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          {links.map(([key, id]) => (
            <a
              key={key}
              href={`#${id}`}
              data-testid={`nav-link-${key}`}
              className="hover:text-orange-600 transition"
            >
              {t(`nav.${key}`)}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a
            href="https://www.facebook.com/profile.php?id=61579736596157"
            target="_blank"
            rel="noreferrer"
            aria-label="Facebook"
            data-testid="nav-facebook-btn"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-neutral-800/20 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition"
          >
            <Facebook className="w-4 h-4" />
          </a>
          <LanguageSwitcher />
          <a
            href={buildWhatsappLink("Hi! I'd like to book a moto.")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-orange-500 hover:text-neutral-900 transition"
          >
            {t("nav.book")}
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden p-2"
            aria-label="menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-white border-t border-black/5 shadow-xl"
          >
            <div className="flex flex-col p-5 gap-1">
              {links.map(([key, id]) => (
                <a
                  key={key}
                  href={`#${id}`}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${key}`}
                  className="text-base font-semibold py-3 border-b border-black/5 last:border-b-0 hover:text-orange-600"
                >
                  {t(`nav.${key}`)}
                </a>
              ))}
              <a
                href={buildWhatsappLink("Hi! I'd like to book a moto.")}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-book"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-neutral-900 text-white px-5 py-3 rounded-full text-sm font-bold hover:bg-orange-500 transition"
              >
                {t("nav.book")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

/* --------------------------- Hero Slider --------------------------- */
const HeroSlider = () => {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const total = HERO_IMAGES.length;

  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(
    () => setIdx((i) => (i - 1 + total) % total),
    [total]
  );

  useEffect(() => {
    // Preload all hero images so slides don't flash black
    HERO_IMAGES.forEach((im) => {
      const i = new Image();
      i.src = im.src;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(next, 4000);
    return () => clearInterval(id);
  }, [next]);

  return (
    <section
      id="home"
      className="relative h-[100svh] w-full overflow-hidden bg-neutral-950"
    >
      {/* Slides — soft crossfade + slow Ken Burns pan/zoom */}
      {HERO_IMAGES.map((img, i) => {
        const active = i === idx;
        return (
          <div
            key={i}
            aria-hidden={!active}
            className={`absolute inset-0 transition-opacity ease-[cubic-bezier(0.4,0,0.2,1)] duration-[1200ms] ${
              active ? "opacity-100 z-[1]" : "opacity-0 z-0"
            }`}
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="eager"
              fetchPriority={i === 0 ? "high" : "auto"}
              decoding="async"
              className={`w-full h-full object-cover will-change-transform ${
                active ? "hero-kenburns" : ""
              }`}
            />
          </div>
        );
      })}

      {/* Readability overlays (fixed — do NOT crossfade) */}
      <div className="absolute inset-0 z-[2] pointer-events-none bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
      <div className="absolute inset-0 z-[2] pointer-events-none bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute inset-0 z-[2] pointer-events-none bg-black/15" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 h-full flex flex-col justify-end pb-24 pt-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6 shadow-lg shadow-orange-500/30"
          >
            <MapPin className="w-3.5 h-3.5" />
            {t("hero.tag")}
          </motion.div>
          <h1 className="hero-title font-heading text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {t("hero.title1")}
            <br />
            <Typewriter
              key={t("hero.title2")}
              text={t("hero.title2")}
              className="italic text-orange-300"
              speed={70}
              delay={700}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.6 }}
            className="hero-sub text-white text-base sm:text-lg mt-6 max-w-xl"
          >
            {t("hero.subtitle")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.9, duration: 0.6 }}
            className="flex flex-wrap items-center gap-3 mt-8"
          >
            <a
              href={buildWhatsappLink("Hi! I want to rent a moto.")}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 bg-orange-500 text-neutral-900 px-6 py-3.5 rounded-full font-bold hover:bg-orange-400 transition shadow-xl shadow-orange-500/30"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 2C6.486 2 2 6.486 2 12c0 1.762.462 3.415 1.267 4.851L2 22l5.36-1.245A9.955 9.955 0 0 0 12 22c5.514 0 10-4.486 10-10S17.514 2 12 2zm0 18a7.94 7.94 0 0 1-4.032-1.098l-.29-.172-3.181.739.681-3.099-.19-.298A7.95 7.95 0 0 1 4 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
              </svg>
              {t("hero.cta")}
            </a>
            <a
              href="#moto"
              data-testid="hero-cta-motos"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white px-6 py-3.5 rounded-full font-semibold hover:bg-white hover:text-neutral-900 transition"
            >
              {t("hero.ctaAlt")}
            </a>
          </motion.div>
        </motion.div>

        {/* controls */}
        <div className="absolute bottom-8 right-8 flex items-center gap-3">
          <button
            data-testid="hero-slide-prev"
            onClick={prev}
            aria-label="prev"
            className="w-12 h-12 rounded-full border border-white/40 text-white hover:bg-white hover:text-neutral-900 flex items-center justify-center transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="hero-slide-next"
            onClick={next}
            aria-label="next"
            className="w-12 h-12 rounded-full border border-white/40 text-white hover:bg-white hover:text-neutral-900 flex items-center justify-center transition"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="absolute bottom-8 left-6 sm:left-10 flex gap-1.5">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "bg-orange-400 w-8" : "bg-white/50 w-3"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ About ------------------------------ */
const AboutSection = () => {
  const { t } = useTranslation();
  const features = [
    { icon: <Languages className="w-5 h-5" />, key: "f1" },
    { icon: <ShieldCheck className="w-5 h-5" />, key: "f2" },
    { icon: <Truck className="w-5 h-5" />, key: "f3" },
    { icon: <Calendar className="w-5 h-5" />, key: "f4" },
  ];
  return (
    <section
      id="about"
      className="py-24 sm:py-32 px-6 sm:px-10 bg-[#faf7f2]"
      data-testid="about-section"
    >
      <Reveal className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <RevealItem>
          <div className="text-orange-600 uppercase tracking-[0.25em] text-xs font-bold mb-4">
            {t("about.kicker")}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("about.title")}
          </h2>
          <p className="text-neutral-600 mt-6 text-base sm:text-lg leading-relaxed max-w-lg">
            {t("about.body")}
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {features.map((f, i) => (
              <motion.div
                key={f.key}
                data-testid={`about-feature-${f.key}`}
                variants={revealChild}
                whileHover={{ y: -4, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-black/5 hover:border-orange-500 hover:shadow-lg cursor-default"
              >
                <div className="w-10 h-10 rounded-full bg-orange-500/10 text-orange-600 flex items-center justify-center">
                  {f.icon}
                </div>
                <div className="font-semibold text-sm">
                  {t(`about.${f.key}`)}
                </div>
              </motion.div>
            ))}
          </div>
        </RevealItem>
        <RevealItem className="relative">
          <motion.img
            src="https://customer-assets.emergentagent.com/job_moto-navi-hub/artifacts/ejzdix26_8ef3fc78-a25e-4273-8f1a-64259afed568.JPG"
            alt="Jhossy fleet — 100% Honda"
            initial={{ opacity: 0, scale: 1.08, filter: "blur(18px)" }}
            whileInView={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.03, rotate: -0.5 }}
            className="about-photo w-full aspect-[4/3] object-cover object-center rounded-3xl shadow-2xl"
          />
          <motion.img
            src={LOGO_URL}
            alt="Jhossy logo"
            initial={{ rotate: -8, opacity: 0, scale: 0.6 }}
            whileInView={{ rotate: 0, opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
            whileHover={{ rotate: -6, scale: 1.05 }}
            className="absolute -bottom-6 -left-6 w-28 h-28 bg-white rounded-2xl p-3 shadow-xl border border-black/5"
          />
          <motion.div
            initial={{ rotate: 10, opacity: 0, scale: 0.6 }}
            whileInView={{ rotate: 0, opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.55, type: "spring", stiffness: 200 }}
            whileHover={{ rotate: 6 }}
            className="absolute -top-6 -right-6 bg-orange-400 rounded-2xl px-5 py-4 shadow-xl"
          >
            <div className="font-heading text-3xl leading-none">100%</div>
            <div className="text-xs uppercase font-bold tracking-wider mt-1">
              Honda
            </div>
          </motion.div>
        </RevealItem>
      </Reveal>
    </section>
  );
};

/* ------------------------------ Motos ------------------------------ */
const MotoCard = ({ moto }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      data-testid={`moto-card-${moto.id}`}
      variants={revealChild}
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 200, damping: 22 }}
      className="group rounded-3xl overflow-hidden bg-white border border-black/5 shadow-sm hover:shadow-2xl"
    >
      <div
        className={`relative aspect-[4/3] overflow-hidden ${
          moto.productShot ? "bg-neutral-50" : "bg-neutral-100"
        }`}
      >
        <img
          src={moto.image}
          alt={t(moto.nameKey)}
          style={
            moto.imageScale ? { transform: `scale(${moto.imageScale})` } : undefined
          }
          className={`w-full h-full group-hover:scale-110 transition duration-[900ms] ease-out ${
            moto.productShot ? "object-contain p-4" : "object-cover"
          }`}
        />
        <div
          className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
          style={{
            backgroundColor: moto.accent,
            color: moto.accent === "#fb923c" ? "#111" : "#fff",
          }}
        >
          {t(moto.engineKey)}
        </div>
      </div>
      <div className="p-6 sm:p-8">
        <h3 className="font-heading text-3xl sm:text-4xl tracking-tight">
          {t(moto.nameKey)}
        </h3>
        <p className="text-neutral-600 mt-3 text-sm leading-relaxed">
          {t(moto.descKey)}
        </p>
        <div className="flex flex-wrap gap-2 mt-5">
          {moto.specs.map((s) => (
            <span
              key={s}
              className="px-3 py-1 bg-neutral-100 text-neutral-700 text-xs font-semibold rounded-full"
            >
              {s}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-6">
          {[
            ["day", moto.prices.day],
            ["week", moto.prices.week],
            ["month", moto.prices.month],
          ].map(([k, v]) => (
            <div
              key={k}
              className="rounded-xl border border-black/10 p-3 text-center"
            >
              <div className="font-heading text-xl">${v}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
                {t(`moto.${k}`)}
              </div>
            </div>
          ))}
        </div>
        <a
          href={buildWhatsappLink(`Hi! I want to book a ${t(moto.nameKey)}.`)}
          target="_blank"
          rel="noreferrer"
          data-testid={`moto-book-${moto.id}`}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-neutral-900 text-white py-3.5 rounded-full font-semibold hover:bg-orange-500 hover:text-neutral-900 transition"
        >
          {t("moto.book")}
        </a>
      </div>
    </motion.div>
  );
};

/* ---------------------------- Packages ---------------------------- */
const PackageCard = ({ pkg, index }) => {
  const { t } = useTranslation();
  const msg = `Hola! Quiero reservar el ${t(pkg.badge)} (${pkg.bikes} motos · ${pkg.days} días · $${pkg.price}).`;
  return (
    <motion.div
      variants={revealChild}
      whileHover={{ y: -10 }}
      transition={{ type: "spring", stiffness: 200, damping: 22 }}
      data-testid={`package-card-${pkg.id}`}
      className={`relative rounded-3xl p-8 flex flex-col shadow-sm hover:shadow-2xl transition-shadow ${
        pkg.highlight
          ? "bg-neutral-950 text-white border-2 border-orange-400"
          : "bg-white border border-black/5"
      }`}
    >
      {pkg.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-orange-400 text-neutral-900 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest shadow-lg">
          <Sparkles className="w-3.5 h-3.5" />
          {t("packages.popular")}
        </div>
      )}
      <div
        className={`inline-flex self-start items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${
          pkg.highlight
            ? "bg-white/10 text-white"
            : "bg-orange-500/10 text-orange-600"
        }`}
      >
        <Users className="w-3.5 h-3.5" />
        {t(pkg.badge)}
      </div>
      <div className="mt-6 flex items-baseline gap-2">
        <div className="font-heading text-6xl leading-none">${pkg.price}</div>
        <div className={`text-xs uppercase tracking-widest font-semibold ${pkg.highlight ? "text-white/60" : "text-neutral-500"}`}>
          USD
        </div>
      </div>
      <div
        className={`mt-2 text-sm font-semibold ${
          pkg.highlight ? "text-orange-300" : "text-orange-600"
        }`}
      >
        {pkg.bikes} {t("packages.bikes")} · {pkg.days} {t("packages.days")}
      </div>

      <div
        className={`mt-6 text-[11px] uppercase tracking-widest font-bold ${
          pkg.highlight ? "text-white/50" : "text-neutral-400"
        }`}
      >
        {t("packages.includes.title")}
      </div>
      <ul className="mt-3 space-y-2.5 text-sm">
        {[
          t("packages.includes.bikes", { n: pkg.bikes }),
          t("packages.includes.helmets"),
          t("packages.includes.assist"),
        ].map((line) => (
          <li key={line} className="flex items-start gap-2.5">
            <Check
              className={`w-4 h-4 mt-0.5 shrink-0 ${
                pkg.highlight ? "text-orange-300" : "text-orange-600"
              }`}
            />
            <span className={pkg.highlight ? "text-white/85" : "text-neutral-700"}>
              {line}
            </span>
          </li>
        ))}
      </ul>

      <p
        className={`mt-6 text-sm leading-relaxed ${
          pkg.highlight ? "text-white/70" : "text-neutral-500"
        }`}
      >
        {t(pkg.perfectFor)}
      </p>

      <a
        href={buildWhatsappLink(msg)}
        target="_blank"
        rel="noreferrer"
        data-testid={`package-book-${pkg.id}`}
        className={`mt-8 inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold transition ${
          pkg.highlight
            ? "bg-orange-400 text-neutral-900 hover:bg-white"
            : "bg-neutral-900 text-white hover:bg-orange-500"
        }`}
      >
        {t("packages.book")}
      </a>
    </motion.div>
  );
};

const PackagesSection = () => {
  const { t } = useTranslation();
  return (
    <section
      id="packages"
      className="py-24 sm:py-32 px-6 sm:px-10 bg-[#faf7f2]"
      data-testid="packages-section"
    >
      <Reveal className="max-w-7xl mx-auto">
        <RevealItem className="max-w-2xl">
          <div className="text-orange-600 uppercase tracking-[0.25em] text-xs font-bold mb-4">
            {t("packages.kicker")}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("packages.title")}
          </h2>
          <p className="text-neutral-600 mt-4 text-base sm:text-lg">
            {t("packages.subtitle")}
          </p>
        </RevealItem>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-14 items-stretch">
          {PACKAGES.map((p, i) => (
            <PackageCard key={p.id} pkg={p} index={i} />
          ))}
        </div>
      </Reveal>
    </section>
  );
};

/* ---------------------------- Motorcycles ---------------------------- */
const MotoSection = () => {
  const { t } = useTranslation();
  return (
    <section
      id="moto"
      className="py-24 sm:py-32 px-6 sm:px-10 bg-white"
      data-testid="moto-section"
    >
      <Reveal className="max-w-7xl mx-auto">
        <RevealItem className="max-w-2xl">
          <div className="text-orange-600 uppercase tracking-[0.25em] text-xs font-bold mb-4">
            {t("moto.kicker")}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("moto.title")}
          </h2>
          <p className="text-neutral-600 mt-4 text-base sm:text-lg">
            {t("moto.subtitle")}
          </p>
        </RevealItem>
        <div className="grid md:grid-cols-2 gap-8 mt-14">
          {MOTORCYCLES.map((m) => (
            <MotoCard key={m.id} moto={m} />
          ))}
        </div>
      </Reveal>
    </section>
  );
};

/* ------------------------------ Rates ------------------------------ */
const RatesSection = () => {
  const { t } = useTranslation();
  return (
    <section
      id="rates"
      className="py-24 sm:py-32 px-6 sm:px-10 bg-neutral-950 text-white"
      data-testid="rates-section"
    >
      <Reveal className="max-w-7xl mx-auto">
        <RevealItem className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <div className="text-orange-400 uppercase tracking-[0.25em] text-xs font-bold mb-4">
              {t("rates.kicker")}
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("rates.title")}
            </h2>
            <p className="text-neutral-400 mt-4 text-base sm:text-lg max-w-lg">
              {t("rates.subtitle")}
            </p>
          </div>
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            href={buildWhatsappLink("Hi! I want to check availability.")}
            target="_blank"
            rel="noreferrer"
            data-testid="rates-book-btn"
            className="self-start lg:self-auto inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-full font-bold hover:bg-orange-400 transition"
          >
            {t("nav.book")}
          </motion.a>
        </RevealItem>

        <RevealItem className="mt-14 overflow-hidden rounded-3xl border border-white/10">
          <table className="w-full text-left">
            <thead className="bg-white/5">
              <tr>
                <th className="p-5 font-heading text-lg">Moto</th>
                <th className="p-5 font-semibold text-sm uppercase tracking-widest text-orange-400">
                  {t("rates.day")}
                </th>
                <th className="p-5 font-semibold text-sm uppercase tracking-widest text-orange-400">
                  {t("rates.week")}
                </th>
                <th className="p-5 font-semibold text-sm uppercase tracking-widest text-orange-400">
                  {t("rates.month")}
                </th>
              </tr>
            </thead>
            <tbody>
              {MOTORCYCLES.map((m) => (
                <tr
                  key={m.id}
                  data-testid={`rates-row-${m.id}`}
                  className="border-t border-white/10 hover:bg-white/5 transition"
                >
                  <td className="p-5">
                    <div className="font-heading text-xl">{t(m.nameKey)}</div>
                    <div className="text-sm text-neutral-400">
                      {t(m.engineKey)}
                    </div>
                  </td>
                  <td className="p-5 font-heading text-2xl">
                    ${m.prices.day}
                  </td>
                  <td className="p-5 font-heading text-2xl">
                    ${m.prices.week}
                  </td>
                  <td className="p-5 font-heading text-2xl">
                    ${m.prices.month}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </RevealItem>
      </Reveal>
    </section>
  );
};

/* ------------------------------ Gallery ------------------------------ */
const GallerySection = () => {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(null);
  return (
    <section
      id="gallery"
      className="py-24 sm:py-32 px-6 sm:px-10 bg-[#faf7f2]"
      data-testid="gallery-section"
    >
      <Reveal className="max-w-7xl mx-auto">
        <RevealItem className="max-w-2xl mb-14">
          <div className="text-orange-600 uppercase tracking-[0.25em] text-xs font-bold mb-4">
            {t("gallery.kicker")}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("gallery.title")}
          </h2>
          <p className="text-neutral-600 mt-4 text-base sm:text-lg">
            {t("gallery.subtitle")}
          </p>
        </RevealItem>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {GALLERY_IMAGES.map((img, i) => (
            <motion.button
              key={i}
              data-testid={`gallery-img-${i}`}
              onClick={() => setLightbox(img)}
              variants={revealChild}
              whileHover={{ y: -6, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className={`group relative overflow-hidden rounded-2xl bg-neutral-200 shadow-sm hover:shadow-2xl ${
                i % 5 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"
              }`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition duration-[900ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />
              <div className="absolute bottom-3 left-3 right-3 text-white text-xs font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition duration-500 text-left">
                {img.alt}
              </div>
            </motion.button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4"
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-white p-2"
              data-testid="lightbox-close"
            >
              <X className="w-7 h-7" />
            </button>
            <motion.img
              key={lightbox.src}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 25 }}
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-h-[90vh] max-w-full object-contain rounded-lg"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

/* --------------------------- Email modal --------------------------- */
const EmailModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const subject = `Jhossy Moto Rental — ${name || "Booking inquiry"}`;
    const body = `${message}\n\n— ${name}`;
    window.location.href = `mailto:${LOCATION.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    onClose();
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="email-modal-backdrop"
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ type: "spring", stiffness: 260, damping: 25 }}
            data-testid="email-modal"
            className="w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl relative"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="email-modal-close"
              className="absolute top-4 right-4 p-2 text-neutral-500 hover:text-neutral-900"
              aria-label="close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="inline-flex items-center gap-2 bg-orange-500/10 text-orange-600 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
              <Mail className="w-3.5 h-3.5" />
              Email
            </div>
            <h3 className="font-heading text-3xl mt-4 tracking-tight">
              {t("contact.emailTitle")}
            </h3>
            <p className="text-neutral-500 text-sm mt-2">
              {t("contact.emailSubtitle")}
            </p>
            <div className="text-xs text-neutral-400 mt-3 break-all">
              {LOCATION.email}
            </div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="email-input-name"
              placeholder={t("contact.emailPlaceholderName")}
              className="w-full mt-6 rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-orange-500 transition"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="email-input-message"
              rows={4}
              placeholder={t("contact.emailPlaceholderMessage")}
              className="w-full mt-3 rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-orange-500 transition resize-none"
            />
            <button
              type="submit"
              data-testid="email-submit"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-neutral-900 text-white py-3.5 rounded-full font-bold hover:bg-orange-500 transition"
            >
              <Send className="w-4 h-4" />
              {t("contact.emailSend")}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---------------------------- Location ---------------------------- */
const LocationSection = () => {
  const { t } = useTranslation();
  const [emailOpen, setEmailOpen] = useState(false);
  return (
    <section
      id="contact"
      className="py-24 sm:py-32 px-6 sm:px-10 bg-white"
      data-testid="location-section"
    >
      <Reveal className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-stretch">
        <RevealItem className="flex flex-col justify-center">
          <div className="text-orange-600 uppercase tracking-[0.25em] text-xs font-bold mb-4">
            {t("location.kicker")}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("location.title")}
          </h2>
          <p className="text-neutral-600 mt-4 text-base sm:text-lg max-w-md">
            {t("location.body")}
          </p>
          <div className="mt-8 space-y-4">
            <motion.div
              whileHover={{ x: 6 }}
              className="flex items-start gap-4"
            >
              <div className="w-11 h-11 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-600">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold">
                  {LOCATION.city}, {LOCATION.country}
                </div>
                <div className="text-sm text-neutral-600 mt-0.5 max-w-xs leading-snug">
                  {LOCATION.address}
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <a
                    href={LOCATION.mapLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="location-map-link"
                    className="text-sm text-orange-600 hover:underline font-semibold"
                  >
                    Google Maps →
                  </a>
                  <a
                    href={LOCATION.directionsLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="location-directions-link"
                    className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:underline font-semibold"
                  >
                    <Navigation className="w-3.5 h-3.5" />
                    {t("location.directions")}
                  </a>
                </div>
              </div>
            </motion.div>
            <motion.div
              whileHover={{ x: 6 }}
              className="flex items-start gap-4"
            >
              <div className="w-11 h-11 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-600">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold">{t("location.hours")}</div>
                <div className="text-sm text-neutral-500 mt-1 max-w-sm leading-snug">
                  {t("location.hoursSubtitle")}
                </div>
              </div>
            </motion.div>
            <motion.div
              whileHover={{ x: 6 }}
              className="flex items-start gap-4"
            >
              <div className="w-11 h-11 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-600">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="location-whatsapp-link"
                  className="font-semibold hover:text-orange-600"
                >
                  {WHATSAPP_DISPLAY}
                </a>
                <div className="text-sm text-neutral-500">WhatsApp</div>
              </div>
            </motion.div>
            <motion.div
              whileHover={{ x: 6 }}
              className="flex items-start gap-4"
            >
              <div className="w-11 h-11 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-600">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => setEmailOpen(true)}
                  data-testid="location-email-btn"
                  className="font-semibold hover:text-orange-600 text-left"
                >
                  {LOCATION.email}
                </button>
                <div className="text-sm text-neutral-500">Email</div>
              </div>
            </motion.div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <motion.a
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              href={buildWhatsappLink("Hi! I want to book a moto.")}
              target="_blank"
              rel="noreferrer"
              data-testid="location-cta"
              className="inline-flex items-center gap-2 bg-neutral-900 text-white px-6 py-3.5 rounded-full font-bold hover:bg-orange-500 transition"
            >
              {t("contact.cta")}
            </motion.a>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              type="button"
              onClick={() => setEmailOpen(true)}
              data-testid="location-email-cta"
              className="inline-flex items-center gap-2 bg-white border-2 border-neutral-900 text-neutral-900 px-6 py-3.5 rounded-full font-bold hover:bg-orange-500 hover:border-orange-500 hover:text-white transition"
            >
              <Mail className="w-4 h-4" />
              {t("contact.emailCta")}
            </motion.button>
          </div>
        </RevealItem>
        <RevealItem className="rounded-3xl overflow-hidden shadow-2xl border border-black/5 min-h-[420px]">
          <iframe
            title="Jhossy Location"
            src={LOCATION.mapEmbed}
            className="w-full h-full min-h-[420px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </RevealItem>
      </Reveal>
      <EmailModal open={emailOpen} onClose={() => setEmailOpen(false)} />
    </section>
  );
};

/* ---------------------------- Payments ---------------------------- */
const PaymentSection = () => {
  const { t } = useTranslation();
  return (
    <section
      id="payments"
      className="py-24 sm:py-28 px-6 sm:px-10 bg-white"
      data-testid="payments-section"
    >
      <Reveal className="max-w-6xl mx-auto">
        <RevealItem className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-orange-500 to-orange-700 text-white p-10 sm:p-14 shadow-2xl">
          <div
            className="absolute inset-0 opacity-15 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, white 0.5px, transparent 0.5px)",
              backgroundSize: "22px 22px",
            }}
          />
          <div className="relative">
            <div className="uppercase tracking-[0.25em] text-xs font-bold text-orange-100 mb-4">
              {t("payments.kicker")}
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("payments.title")}
            </h2>
            <p className="text-white/90 mt-5 text-base sm:text-lg max-w-2xl leading-relaxed">
              {t("payments.body")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-2 rounded-full text-sm font-semibold">
                <Zap className="w-4 h-4" /> {t("payments.tag")}
              </span>
              <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-2 rounded-full text-sm font-semibold">
                <CreditCard className="w-4 h-4" /> {t("payments.cards")}
              </span>
            </div>
          </div>
        </RevealItem>
      </Reveal>
    </section>
  );
};

/* ------------------------------ Footer ------------------------------ */
const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-neutral-950 text-white px-6 sm:px-10 py-14">
      <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-10 items-start">
        <div className="flex items-start gap-4">
          <img
            src={LOGO_URL}
            alt="Jhossy Moto Rental"
            className="w-20 h-20 bg-white rounded-xl p-2 object-contain"
          />
          <div>
            <div className="font-heading text-2xl leading-none">JHOSSY</div>
            <div className="text-xs uppercase tracking-widest text-orange-400 mt-1">
              Moto Rental · SJDS
            </div>
            <p className="text-neutral-400 text-sm mt-3 max-w-xs">
              {t("footer.tagline")}
            </p>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-orange-400 mb-3">
            {t("nav.contact")}
          </div>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            className="block font-semibold text-lg hover:text-orange-400"
          >
            {WHATSAPP_DISPLAY}
          </a>
          <a
            href={`mailto:${LOCATION.email}`}
            data-testid="footer-email"
            className="mt-2 block text-sm text-neutral-300 hover:text-orange-400 break-all"
          >
            {LOCATION.email}
          </a>
          <div className="text-neutral-400 text-sm mt-2">
            {t("location.hours")}
          </div>
        </div>
        <div>
          <a
            href="https://www.JhossyMotoRentalSJDS.com"
            className="font-semibold hover:text-orange-400 break-all"
          >
            JhossyMotoRentalSJDS.com
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=61579736596157"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-facebook"
            className="mt-3 inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-[#1877F2]"
          >
            <Facebook className="w-4 h-4" />
            Facebook
          </a>
        </div>
      </div>
      <div className="max-w-7xl mx-auto border-t border-white/10 mt-10 pt-6 text-neutral-500 text-xs flex flex-col sm:flex-row justify-between gap-2">
        <div>
          © {new Date().getFullYear()} Jhossy Moto Rental. {t("footer.rights")}
        </div>
        <div>
          Website created by{" "}
          <a
            href="https://www.GregMiceli.com"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-credit"
            className="text-orange-400 font-semibold hover:text-white"
          >
            www.GregMiceli.com
          </a>
        </div>
        <div>San Juan del Sur · Nicaragua 🇳🇮</div>
      </div>
    </footer>
  );
};

/* ------------------------- Floating Buttons ------------------------- */
const WhatsAppFloating = () => {
  const { t } = useTranslation();
  return (
    <a
      href={buildWhatsappLink("Hi! I'd like info about renting a moto.")}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-floating"
      className="fixed bottom-6 right-6 z-50 group inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5b] text-white pl-4 pr-5 py-3.5 rounded-full shadow-2xl shadow-[#25D366]/40 hover:scale-105 transition-transform"
    >
      <span className="relative flex items-center justify-center">
        <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
        <svg viewBox="0 0 24 24" className="w-6 h-6 relative" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
          <path d="M12 2C6.486 2 2 6.486 2 12c0 1.762.462 3.415 1.267 4.851L2 22l5.36-1.245A9.955 9.955 0 0 0 12 22c5.514 0 10-4.486 10-10S17.514 2 12 2zm0 18a7.94 7.94 0 0 1-4.032-1.098l-.29-.172-3.181.739.681-3.099-.19-.298A7.95 7.95 0 0 1 4 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
        </svg>
      </span>
      <span className="font-bold text-sm hidden sm:inline">
        {t("common.callNow")}
      </span>
    </a>
  );
};

const ScrollTop = () => {
  const [show, setShow] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="scroll-top-btn"
          className="fixed bottom-6 left-6 z-50 inline-flex flex-col items-center gap-1 bg-neutral-900 text-white p-3.5 rounded-full shadow-xl hover:bg-orange-500 hover:text-neutral-900 transition"
          aria-label={t("common.scrollTop")}
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

/* ------------------------------- App ------------------------------- */
function App() {
  return (
    <div className="min-h-screen bg-white antialiased" data-testid="app-root">
      <NavBar />
      <main>
        <HeroSlider />
        <AboutSection />
        <PackagesSection />
        <MotoSection />
        <RatesSection />
        <GallerySection />
        <LocationSection />
        <PaymentSection />
      </main>
      <Footer />
      <WhatsAppFloating />
      <ScrollTop />
    </div>
  );
}

export default App;
